import React, { Fragment, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { forEach } from 'lodash';

import ToolkitButton from './ToolkitButton';
import MobileCloseButton from './MobileCloseButton';

import {
  Accessibility,
  Annotations,
  Calculator,
  FormulaSheet,
  Help,
  PeriodicTable,
} from './ToolkitIcons';

import './Toolkit.scss';
import './accessibilityOverride.scss';
import Zoom from './Zoom';
import Bookmark from './Bookmark';
import FloatingActionButton from 'chameleon/ui-stack/ui/base/floating-action-button';

const Toolkit = (props) => {
  const {
    app,
    onShowModal,
    showAnnotationIcon,
    toggleAnnotations,
    toggleFormulaSheet,
    togglePeriodicTable,
    isMobile,
    isPreApSubject,
    formulaSheetOpen,
    periodicTableOpen,
    showFormulaSheetIcon,
    showPeriodicTableIcon,
    questionChanged,
    showQuestionBookmark,
    learnosityData,
    isImpersonating,
    bookmarks,
    addBookmark,
    removeBookmark,
    playerContainer: mainPlayerContainer,
  } = props;
  const [canUseCalculator, setCanUseCalculator] = useState(false);
  const [calculatorActive, setCalculatorActive] = useState(false);
  const [calculatorLoading, setCalculatorLoading] = useState(true);
  const [formulaSheetActive, setFormulaSheetActive] =
    useState(formulaSheetOpen);
  const [periodicTableActive, setPeriodicTableActive] =
    useState(periodicTableOpen);
  const [annotationsActive, setAnnotationsActive] = useState(false);
  const [calculatorWidget, setCalculatorWidget] = useState('');
  const formulaSheetButtonRef = useRef(null);
  const periodicTableButtonRef = useRef(null);
  const bookmarkButtonRef = useRef(null);
  const zoomButtonRef = useRef(null);

  let customWidgetScientific = 'custom-widget-0';
  let customWidgetGraphing = 'custom-widget-1';

  const hideCalculator = async (widget, shouldToggle = true) => {
    const assessAppFeatures = await app.assessApp().features();
    const calculatorInstance = assessAppFeatures[widget];

    setCalculatorActive(false);
    let calculatorIcon = document.querySelector(
      '.ToolkitButton.CalculatorButton svg',
    );
    calculatorIcon.classList.remove('is-highlighted');

    const calculatorContainers = document.querySelectorAll(
      '.desmos-calculator-container',
    );
    forEach(calculatorContainers, (calculatorElement) => {
      calculatorElement.classList.remove('with-calculator');
    });

    if (shouldToggle) calculatorInstance.toggle();

    if (isMobile) {
      const playerContainer = document.querySelector('.Player');
      playerContainer.classList.remove('with-calculator');

      document.querySelector('.calculator-close-button div').style.display =
        'none';
      document.body.style.overflow = 'visible';
      document.body.classList.remove('desmos-calculator-open');
    }
  };

  const getCalculatorWidget = (isScientific, isGraphing) => {
    if (isScientific) {
      return customWidgetScientific;
    } else if (isGraphing) {
      return customWidgetGraphing;
    }
    return '';
  };

  const loadCalculatorTags = async () => {
    const { reference } = app.getCurrentItem();
    let itemTags = await app.getTags()[reference];
    // sometimes items don't have tags. (survey questions for example)
    if (!itemTags) return;
    const isScientific = itemTags.indexOf('calculator:desmos_scientific') > -1;
    const isGraphing = itemTags.indexOf('calculator:desmos_graphing') > -1;
    const calculatorType = isScientific || isGraphing;

    if (isPreApSubject) {
      setCanUseCalculator(calculatorType);
    }

    return { isScientific, isGraphing };
  };

  const instanceCalculator = async () => {
    const { isScientific, isGraphing } = await loadCalculatorTags();
    const widget = getCalculatorWidget(isScientific, isGraphing);
    if (isMobile) {
      const close = document.createElement('div');
      close.className = 'calculator-close-button';
      document.body.appendChild(close);
      ReactDOM.render(
        <MobileCloseButton
          style={{
            display: 'none',
            left: isGraphing ? '0' : 'auto',
            right: isGraphing ? 'auto' : '0',
            marginLeft: isGraphing ? '8px' : 'auto',
            marginRight: isGraphing ? 'auto' : '8px',
          }}
          widget={widget}
          onClick={() => hideCalculator(widget)}
        />,
        document.querySelector('.calculator-close-button'),
      );
    }
    setCalculatorWidget(widget);
  };

  const handleOpenAccessibilityDialog = (e, setOpenMobileToolkit) => {
    app.dialogs().accessibility.show();
    setOpenMobileToolkit(false);
  };

  const handleOpenHelpDialog = () => {
    let calculatorType = '';
    if (calculatorWidget === customWidgetScientific) {
      calculatorType = 'scientific';
    } else if (calculatorWidget === customWidgetGraphing) {
      calculatorType = 'graphing';
    }
    onShowModal('HelpMenu', { calculatorType });
  };

  const showCalculator = async (widget) => {
    const assessAppFeatures = await app.assessApp().features();
    const calculatorInstance = assessAppFeatures[widget];

    if (!calculatorInstance) return;

    // PREAP-2877 move calculator inside player to prevent focus conflict
    const playerContainer = document.querySelector('.Player');
    const calculatorContainers = document.querySelectorAll(
      '.desmos-calculator-container',
    );
    forEach(calculatorContainers, (container) => {
      playerContainer.append(container);
    });

    if (!isMobile) {
      if (
        calculatorContainers &&
        calculatorContainers.length &&
        calculatorContainers[calculatorContainers.length - 1]
      ) {
        const closeButton = calculatorContainers[
          calculatorContainers.length - 1
        ].querySelector('.lrn_desmos_calculator_close');
        if (closeButton) {
          closeButton.addEventListener('click', () =>
            hideCalculator(calculatorWidget, false),
          );
        }
      }
    }

    if (isMobile) {
      playerContainer.classList.add('with-calculator');
      document.querySelector('.calculator-close-button div').style.display =
        'flex';
      document.body.style.overflow = 'hidden';
      document.body.classList.add('desmos-calculator-open');
      forEach(calculatorContainers, (container) => {
        container.classList.add('with-calculator');
      });
    }

    calculatorInstance.toggle();
    setCalculatorActive(true);
    let calculatorIcon = document.querySelector(
      '.ToolkitButton.CalculatorButton svg',
    );
    calculatorIcon.classList.add('is-highlighted');
  };

  const handleOpenCalculator = async () => {
    if (calculatorActive) {
      hideCalculator(calculatorWidget);
    } else {
      showCalculator(calculatorWidget);
    }
  };

  const [currentItemReference, setCurrentItemReference] = useState();
  const [currentItemIndex, setCurrentItemIndex] = useState();
  /**
   * Init method
   * */
  const readyListener = () => {
    const {
      learnosityData: { items, app },
    } = props;
    /*
     * We need to use `getCurrentItem()` because when resizing props.learnosityData.currentItem will point to
     * the original opened item.
     * */
    const { reference } = app.getCurrentItem();

    app.on('item:changed', itemChanged);

    setCurrentItemReference(reference);
    setCurrentItemIndex(Object.keys(items).indexOf(reference) + 1);
  };

  /**
   * Learnosity Event handler for when item has changed
   * @param {number} itemIndex The item index
   * */
  const itemChanged = (itemIndex) => {
    const {
      learnosityData: { app },
    } = props;
    const { reference } = app.getCurrentItem();

    setCurrentItemReference(reference);
    setCurrentItemIndex(itemIndex + 1);
  };

  useEffect(() => {
    readyListener();
  }, []);

  const handleOpenAnnotations = ({ currentTarget }) => {
    const active = !annotationsActive;
    toggleAnnotations(active);
    setAnnotationsActive(active);
    const listAction = active ? 'add' : 'remove';
    const button =
      currentTarget.querySelector('.annotationToolkitButton') || currentTarget;
    button.classList[listAction]('is-highlighted');
  };

  const handleOpenFormulaSheet = ({ currentTarget }) => {
    const active = !formulaSheetActive;
    toggleFormulaSheet(active);
    setFormulaSheetActive(active);
    const listAction = active ? 'add' : 'remove';
    const button = currentTarget.querySelector('svg');
    button.classList[listAction]('is-highlighted');
  };

  const handleOpenPeriodicTable = ({ currentTarget }) => {
    const active = !periodicTableActive;
    togglePeriodicTable(active);
    setPeriodicTableActive(active);
    const listAction = active ? 'add' : 'remove';
    const button = currentTarget.querySelector('svg');
    button.classList[listAction]('is-highlighted');
  };

  useEffect(() => {
    loadCalculatorTags();
  }, [questionChanged]);

  useEffect(() => {
    if (canUseCalculator) {
      let calculatorHasLoaded = false;
      const timer = setInterval(() => {
        const features = app.assessApp().features();
        if (features && !calculatorHasLoaded) {
          // the custom widget id's are incremented each time the player is reentered
          // see https://reference.learnosity.com/items-api/methods#assessApp-Append
          // when it's an even number at the end it's scientific, otherwise it's graphing
          Object.keys(features).forEach((key) => {
            if (key.indexOf('custom-widget-') > -1) {
              calculatorHasLoaded = true;
              let customWidgetSplit = key.split('-');
              if (
                parseInt(customWidgetSplit[customWidgetSplit.length - 1], 10) %
                  2 ===
                0
              ) {
                customWidgetScientific = key;
              } else {
                customWidgetGraphing = key;
              }
            }
          });
          if (calculatorHasLoaded) {
            instanceCalculator();
          }
        } else {
          clearInterval(timer);
          setCalculatorLoading(false);
        }
      }, 1000);
    }
  }, [canUseCalculator]);

  useEffect(() => {
    if (showFormulaSheetIcon) {
      setFormulaSheetActive(formulaSheetOpen);
      const listAction = formulaSheetOpen ? 'add' : 'remove';
      const button = formulaSheetButtonRef.current.querySelector('svg');
      button.classList[listAction]('is-highlighted');
    }
  }, [formulaSheetOpen]);

  useEffect(() => {
    if (showPeriodicTableIcon) {
      setPeriodicTableActive(periodicTableOpen);
      const listAction = periodicTableOpen ? 'add' : 'remove';
      const button = periodicTableButtonRef.current.querySelector('svg');
      button.classList[listAction]('is-highlighted');
    }
  }, [periodicTableOpen]);

  useEffect(() => {
    if (calculatorActive && questionChanged) {
      hideCalculator(calculatorWidget);
    }
  }, [questionChanged, calculatorWidget, calculatorActive]);

  const renderMobileToolkit = () => {
    const actions = [];
    canUseCalculator &&
      actions.push({
        onClick: handleOpenCalculator,
        icon: <Calculator isMobile />,
        ariaLabel: 'Calculator',
      });

    showFormulaSheetIcon &&
      actions.push({
        onClick: handleOpenFormulaSheet,
        icon: <FormulaSheet isMobile />,
        ariaLabel: 'Formula Sheet',
      });

    showPeriodicTableIcon &&
      actions.push({
        onClick: handleOpenPeriodicTable,
        icon: <PeriodicTable isMobile />,
        ariaLabel: 'Periodic Table',
      });
    showAnnotationIcon &&
      actions.push({
        onClick: handleOpenAnnotations,
        icon: <Annotations isMobile />,
        ariaLabel: 'Open Annotations',
      });
    showQuestionBookmark &&
      actions.push({
        icon: (
          <Bookmark
            ref={bookmarkButtonRef}
            isMobile={isMobile}
            key={`bookmark-question-${currentItemIndex}`}
            currentItemIndex={currentItemIndex}
            questionReference={currentItemReference}
            activity={learnosityData.activity}
            isImpersonating={isImpersonating}
            isBookmarked={isBookmarked}
            addBookmark={addBookmark}
            removeBookmark={removeBookmark}
          />
        ),
        onClick: () => bookmarkButtonRef.current.onClick(),
        ariaLabel: 'Bookmark',
      });

    actions.push({
      onClick: handleOpenHelpDialog,
      icon: <Help />,
      ariaLabel: 'Help',
    });

    actions.push({
      onClick: handleOpenAccessibilityDialog,
      icon: <Accessibility />,
      ariaLabel: 'Accessibility',
    });

    return (
      <FloatingActionButton className="mobile-toolkit" actions={actions} />
    );
  };

  const isBookmarked = bookmarks.includes(currentItemReference);
  return isMobile ? (
    renderMobileToolkit()
  ) : (
    <div className="Toolkit">
      <Fragment>
        <ToolkitButton
          position="top"
          label="Zoom"
          description="Zoom"
          target="zoom"
          className="zoomButton"
          onClick={() => zoomButtonRef.current && zoomButtonRef.current.click()}
          isButton={false}
        >
          <Zoom
            app={app}
            buttonRef={zoomButtonRef}
            playerContainer={mainPlayerContainer}
          />
        </ToolkitButton>
        <ToolkitButton
          onClick={handleOpenHelpDialog}
          label="Help"
          description="Help"
          target="help"
        >
          <Help accessibility={{ 'aria-hidden': true }} />
        </ToolkitButton>
        <ToolkitButton
          onClick={handleOpenAccessibilityDialog}
          label="Accessibility"
          description="Accessibility"
          target="accessibility"
        >
          <Accessibility accessibility={{ 'aria-hidden': true }} />
        </ToolkitButton>
        {showQuestionBookmark && (
          <Bookmark
            tooltipProps={{
              target: 'bookmarkButton',
              className: 'BookmarkButton',
              description: 'Bookmark',
            }}
            key={`bookmark-question-${currentItemIndex}`}
            currentItemIndex={currentItemIndex}
            questionReference={currentItemReference}
            activity={learnosityData.activity}
            isImpersonating={isImpersonating}
            isBookmarked={isBookmarked}
            addBookmark={addBookmark}
            removeBookmark={removeBookmark}
          />
        )}

        {canUseCalculator && (
          <ToolkitButton
            target="calcButton"
            description="Calculator"
            onClick={handleOpenCalculator}
            label="Calculator"
            className="CalculatorButton"
            disabled={calculatorLoading}
          >
            <Calculator accessibility={{ 'aria-hidden': true }} />
          </ToolkitButton>
        )}
        {showFormulaSheetIcon && (
          <ToolkitButton
            target="formulaSheetButton"
            description="Equation Sheet"
            onClick={handleOpenFormulaSheet}
            label="Formula Sheet"
            ref={formulaSheetButtonRef}
          >
            <FormulaSheet
              isMobile={isMobile}
              accessibility={{ 'aria-hidden': true }}
            />
          </ToolkitButton>
        )}
        {showPeriodicTableIcon && (
          <ToolkitButton
            target="periodicTableButton"
            description="Periodic Table"
            onClick={handleOpenPeriodicTable}
            label="Periodic Table"
            ref={periodicTableButtonRef}
          >
            <PeriodicTable
              isMobile={isMobile}
              accessibility={{ 'aria-hidden': true }}
            />
          </ToolkitButton>
        )}
        {showAnnotationIcon && (
          <ToolkitButton
            target="annotationButton"
            description="Annotation"
            className="annotationToolkitButton"
            onClick={handleOpenAnnotations}
            label="Annotation"
            isActive={annotationsActive}
          >
            <Annotations
              isMobile={isMobile}
              accessibility={{ 'aria-hidden': true }}
            />
          </ToolkitButton>
        )}
      </Fragment>
    </div>
  );
};

export default Toolkit;

Toolkit.defaultProps = {
  showAnnotationIcon: false,
  isMobile: false,
  toggleAnnotations: () => {},
  playerContainer: PropTypes.instanceOf(Element),
};
Toolkit.propTypes = {
  app: PropTypes.object.isRequired,
  onShowModal: PropTypes.func.isRequired,
  showAnnotationIcon: PropTypes.bool,
  toggleAnnotations: PropTypes.func,
  /**
   * Flag for displaying icons on mobile view or desktop view.
   * HelpMenu and Accessibility icons. Are available only on desktop view
   * */
  isMobile: PropTypes.bool,
  playerContainer: document,
};
